import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Inglewood = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.inglewood-calgary:About Paragraph">
          Inglewood,{' '}
          <LinkText href="/rental-listings/city/calgary">Calgary’s</LinkText>{' '}
          oldest neighborhood, was established in 1875 and is located just east
          of downtown along the Bow and Elbow Rivers. Known for its rich
          heritage, Inglewood boasts a vibrant arts scene, historic
          architecture, and a strong sense of community. The area is a hub for
          culture and nature, featuring over 100 shops, galleries, and eateries,
          as well as access to parks and pathways. Its unique blend of history,
          creativity, and green spaces makes Inglewood a sought-after
          destination for residents and visitors alike.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,300 - $1,800',
          bed2: '$1,500 - $2,600',
          bed3: '$2,700 - $3,500'
        }}
        rentReportCity="Alberta"
        rentReportLink="https://liv.rent/blog/rent-reports/alberta/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.inglewood-calgary:Apartment Hunting Paragraph 1">
            Compared to cities like Vancouver and Richmond, it’s fairly easy to
            find relatively cheap rentals in {{ neighborhoodNameWithCity }} -
            although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon. To start your search, you can refer to this
            guide on {{ neighborhoodNameWithCity }}
            neighbourhoods.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.inglewood-calgary:Apartment Hunting Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.inglewood-calgary:Apartments for rent Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Inglewood, Calgary
              </LinkText>
              , appeal to young professionals and creatives seeking a dynamic
              urban lifestyle. These units often feature modern designs within
              historic buildings, offering a unique living experience. Residents
              enjoy proximity to eclectic shops, art galleries, and the Bow
              River pathways, combining convenience with cultural richness.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.inglewood-calgary:Houses for rent Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Inglewood, Calgary
              </LinkText>
              , are ideal for families and individuals desiring more space
              within a historic setting. The neighborhood offers a mix of
              charming heritage homes and modern infills, often with yards and
              close to parks. Living here provides a blend of community feel and
              easy access to downtown amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.inglewood-calgary:Townhouses for rent Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Inglewood, Calgary
              </LinkText>
              , offer a balance between apartment living and detached homes.
              These multi-level units are perfect for professionals and small
              families, featuring contemporary designs and often including
              garages or private patios. Their location provides easy access to
              local boutiques, cafes, and the river pathways.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.inglewood-calgary:Basements for rent Paragraph">
              Basements for rent in Inglewood, Calgary, provide affordable and
              private living spaces, suitable for students and singles. These
              suites typically come with separate entrances and essential
              amenities. Residents benefit from the neighborhood’s vibrant arts
              scene, green spaces, and proximity to public transit.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.inglewood-calgary:Condos for rent Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Inglewood, Calgary
              </LinkText>
              , attract those seeking modern amenities within a historic
              neighborhood. These units often feature contemporary finishes,
              secure parking, and access to fitness facilities. Residents enjoy
              being steps away from art galleries, live music venues, and scenic
              river walks.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.inglewood-calgary:Rooms for rent Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Inglewood, Calgary
              </LinkText>
              , are a cost-effective option for students and newcomers. Often
              part of shared accommodations, these rooms provide access to
              communal kitchens and living areas. The neighborhood’s cultural
              vibrancy and accessibility make it an attractive choice for
              renters.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.inglewood-calgary:Short term rentals Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Inglewood, Calgary
              </LinkText>
              , cater to tourists and business travelers seeking a unique stay.
              Options range from cozy apartments to entire homes, often located
              near key attractions like the Calgary Zoo and local art galleries.
              The area’s blend of history and modernity offers guests an
              authentic Calgary experience.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.inglewood-calgary:Cheap rentals Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in {{ neighborhoodNameWithCity }}, there
              are neighbourhoods where rent is considerably cheaper, so
              exploring these options is a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.inglewood-calgary:Exploring Paragraph">
          Inglewood, Calgary, Alberta, is a neighborhood where history and
          culture converge. Established in 1875, it is home to the{' '}
          <LinkText href="https://www.calgary.ca/parks/inglewood-bird-sanctuary.html">
            Inglewood Bird Sanctuary
          </LinkText>
          , a haven for wildlife enthusiasts, and the{' '}
          <LinkText href="https://bowhabitat.alberta.ca/">
            Bow Habitat Station
          </LinkText>
          , offering interactive environmental exhibits. The area boasts a
          thriving arts scene, with numerous galleries and live music venues.
          Historic sites like{' '}
          <LinkText href="https://www.theconfluence.ca/">Fort Calgary</LinkText>{' '}
          and the{' '}
          <LinkText href="https://www.deanehouse.com/">Deane House</LinkText>{' '}
          add to its charm. Inglewood’s unique blend of heritage, nature, and
          creativity makes it a vibrant community for all.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.inglewood-calgary:Things To Do Paragraph">
          Inglewood offers a plethora of activities for diverse interests.
          Nature lovers can explore the{' '}
          <LinkText href="https://www.calgary.ca/parks/inglewood-bird-sanctuary.html">
            Inglewood Bird Sanctuary
          </LinkText>
          , home to over 270 bird species, and stroll along the{' '}
          <LinkText href="https://www.calgary.ca/bike-walk-roll/pathways.html">
            Bow River pathway
          </LinkText>
          . Art enthusiasts will appreciate the neighborhood’s numerous
          galleries and live music venues, including the{' '}
          <LinkText href="https://ironwoodstage.ca/">
            Ironwood Stage and Grill
          </LinkText>
          . History buffs can visit{' '}
          <LinkText href="https://www.theconfluence.ca/">Fort Calgary</LinkText>{' '}
          and the{' '}
          <LinkText href="https://www.deanehouse.com/">Deane House</LinkText>.
          Shoppers will enjoy the unique boutiques along 9th Avenue SE, offering
          everything from vintage finds to artisan goods. Culinary delights
          abound, with a range of eateries serving diverse cuisines. Inglewood’s
          vibrant atmosphere ensures there’s something for everyone.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.inglewood-calgary:Restaurants Paragraph">
        <Paragraph>
          Inglewood Calgary offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1) <LinkText href="https://www.thenashyyc.com/">The Nash</LinkText> -
          The Nash is a renowned restaurant located in the heart of Inglewood,
          known for its upscale dining experience in a historic setting. Housed
          in a former hotel, it offers a menu featuring wood-fired meats and
          seasonal dishes. The ambiance combines rustic charm with modern
          elegance, making it perfect for special occasions and food
          enthusiasts.
        </Paragraph>
        <Paragraph>
          2) <LinkText href="https://rougecalgary.com/">Rouge</LinkText> - Rouge
          is a fine dining establishment set in a historic house, offering
          French-inspired cuisine with a focus on local ingredients. Signature
          dishes include duck confit and Alberta beef. The romantic setting,
          complete with a beautiful garden, makes it ideal for intimate dinners
          and celebrations.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://wopizza.ca/">Without Papers Pizza</LinkText> -
          Without Papers Pizza is a casual eatery known for its creative,
          thin-crust pizzas and laid-back atmosphere. Popular choices include
          the &quot;Rocket&quot; with arugula and prosciutto. The restaurant’s
          quirky decor and friendly vibe make it a favorite among locals and
          visitors alike.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.inglewood-calgary:Transportation Paragraph">
          Getting around {{ neighborhoodNameWithCity }}, AB, is convenient with
          several transportation options. Calgary Transit operates a network of
          buses and the{' '}
          <LinkText href="https://www.calgarytransit.com/home.html">
            CTrain
          </LinkText>
          , a light rail system with two major lines—the Red Line and the Blue
          Line—connecting key areas of the city. For faster bus service, the{' '}
          <LinkText href="https://www.calgarytransit.com/rider-information/max.html">
            MAX Bus Rapid Transit
          </LinkText>{' '}
          (BRT) routes provide efficient connections. Calgary is also home to an
          extensive network of pathways and bikeways, making cycling a viable
          option. While some areas are walkable, the city is largely designed
          for driving, with major roads and highways like the Trans-Canada
          Highway and Deerfoot Trail providing easy access to different parts of
          Calgary. Whether by transit, bike, or car, {{ neighborhoodName }}{' '}
          offers accessible ways to explore the city.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.inglewood-calgary:Where To Stay Paragraph">
          Accommodation options in {{ neighborhoodNameWithCity }} cater to those
          seeking a more local and immersive experience. This area features a
          mix of boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, offering
          travelers a unique and personalized stay. While large hotels might be
          limited within {{ neighborhoodName }} itself, its proximity to other
          parts of Calgary ensures that a wide range of traditional
          accommodations, from luxury to budget-friendly, are just a short
          distance away. Staying in {{ neighborhoodName }} gives visitors a
          chance to experience the area’s distinct character while remaining
          conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Inglewood
